import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export interface PropertyRow {
  id: string;
  client_id: string;
  address: string | null;
  city: string;
  postal_code: string | null;
  street: string | null;
  street_number: string | null;
  entrance: string | null;
  floor: string | null;
  door_number: string | null;
  district: string | null;
  building_type: string | null;
  object_type: string | null;
  rooms_count: string | null;
  property_size: number | null;
  property_area: number | null;
  purchase_price: number | null;
  edi_real_number: string | null;
  kg_grundbuch: string | null;
  einlagezahl: string | null;
  b_laufnummer: string | null;
  marketing_stage:
    | 'lead'
    | 'aufnahme'
    | 'active'
    | 'pending'
    | 'sold'
    | 'kaufanbot_angenommen'
    | 'notar_termin_geplant'
    | 'notariell_verkauft';
  order_status: string | null;
  primary_image_url: string | null;
  next_action: string | null;
  next_action_type: string | null;
  next_action_due: number | null;
  ownership_locked: boolean;
  created_by: string;
  created_at: number;
  updated_at: number;
  archived_at: number | null;
  /** Aggregated lifecycle indicator derived from related appointments. */
  traffic_light?: 'off' | 'yellow' | 'green' | 'red' | 'none';
  /** Batch AM — Kaufanbot badge on PropertyCard. 'active' = ≥1 draft/sent
   *  offer (✓). 'accepted' = ≥1 accepted/signed/contract_signed (✓✓).
   *  'none' = nothing live or only rejected/expired. */
  kaufanbot_status?: 'accepted' | 'active' | 'none';
  /** Buyers for the tooltip on the badge. Empty when status='none'. */
  kaufanbot_buyers?: Array<{
    name: string | null;
    price: number | null;
    status:
      | 'draft'
      | 'sent'
      | 'accepted'
      | 'rejected'
      | 'expired'
      | 'signed'
      | 'contract_signed'
      | 'closed';
  }>;
  /** Batch AQ — assigned-agent enrichment via the linked client. Null
   *  on properties whose client has no owner, or whose client row is
   *  missing entirely. Drives the agent column on the /properties list
   *  view + the Moji/Svi filter. */
  assigned_agent_id?: string | null;
  assigned_agent_full_name?: string | null;
  assigned_agent_id_short?: string | null;
  assigned_agent_avatar_key?: string | null;
}

export interface PropertyImageRow {
  id: string;
  property_id: string;
  image_url: string;
  storage_key: string | null;
  is_primary: boolean;
  sort_order: number;
  created_at: number;
}

export interface PropertiesFilters {
  search?: string;
  marketingStage?: PropertyRow['marketing_stage'];
  /** v2 — multi-stage filter for the new chip strip on /properties. Encoded
   *  as a CSV under the same `marketing_stage` query param so the backend
   *  route name stays identical (the API parser accepts both). */
  marketingStages?: PropertyRow['marketing_stage'][];
  city?: string;
  district?: string;
  /** Batch AQ — Moji/Svi toggle on /properties. Filters to properties
   *  whose linked client is assigned to the given agent. */
  agentId?: string;
  /** Nur `false` ist gültig: Objekte ohne aktuelles Inserat. Sonst weglassen. */
  onSite?: false;
  includeArchived?: boolean;
  limit?: number;
  offset?: number;
}

export const propertiesKey = (filters: PropertiesFilters = {}) =>
  ['properties', filters] as const;

export interface PropertiesStats {
  total: number;
  archived: number;
  by_marketing_stage: Record<string, number>;
}

// v2 — KPI strip used to derive counts from items.length on the
// paginated list (default 100), so "Gesamt 100 / Aktiv 51 / Leads 46"
// was always a slice of the true 800+ row corpus. This hook reads
// server-side aggregate counts via /properties/stats. Mirrors the
// existing useClientsStats pattern.
export function usePropertiesStats() {
  return useQuery({
    queryKey: ['properties', 'stats'] as const,
    queryFn: async () => {
      const res = await apiFetch('/properties/stats');
      if (!res.ok) throw new Error(`properties_stats_failed_${res.status}`);
      return (await res.json()) as PropertiesStats;
    },
    staleTime: 30_000,
  });
}

export function useProperties(filters: PropertiesFilters = {}) {
  return useQuery({
    queryKey: propertiesKey(filters),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.marketingStages && filters.marketingStages.length > 0) {
        params.set('marketing_stage', filters.marketingStages.join(','));
      } else if (filters.marketingStage) {
        params.set('marketing_stage', filters.marketingStage);
      }
      if (filters.city) params.set('city', filters.city);
      if (filters.district) params.set('district', filters.district);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.onSite === false) params.set('onSite', 'false');
      if (filters.includeArchived) params.set('include_archived', 'true');
      if (filters.limit) params.set('limit', String(filters.limit));
      if (filters.offset) params.set('offset', String(filters.offset));
      const res = await apiFetch(`/properties?${params}`);
      if (!res.ok) throw new Error(`properties_list_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyRow[] };
      return json.items;
    },
    staleTime: 30_000,
  });
}

// Batch AH faza 2 — infinite scroll variant. Page size defaults to 50;
// stops when last page returns fewer rows than requested.
export function useInfiniteProperties(
  filters: Omit<PropertiesFilters, 'offset' | 'limit'> = {},
  pageSize = 50,
  options: { enabled?: boolean } = {},
) {
  return useInfiniteQuery({
    enabled: options.enabled ?? true,
    queryKey: ['properties', 'infinite', { ...filters, pageSize }] as const,
    queryFn: async ({ pageParam }) => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.marketingStages && filters.marketingStages.length > 0) {
        params.set('marketing_stage', filters.marketingStages.join(','));
      } else if (filters.marketingStage) {
        params.set('marketing_stage', filters.marketingStage);
      }
      if (filters.city) params.set('city', filters.city);
      if (filters.district) params.set('district', filters.district);
      if (filters.agentId) params.set('agent_id', filters.agentId);
      if (filters.onSite === false) params.set('onSite', 'false');
      if (filters.includeArchived) params.set('include_archived', 'true');
      params.set('limit', String(pageSize));
      params.set('offset', String(pageParam ?? 0));
      const res = await apiFetch(`/properties?${params}`);
      if (!res.ok) throw new Error(`properties_list_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyRow[] };
      return { items: json.items, offset: (pageParam ?? 0) as number };
    },
    initialPageParam: 0 as number,
    getNextPageParam: (lastPage) =>
      lastPage.items.length < pageSize
        ? undefined
        : lastPage.offset + pageSize,
    staleTime: 30_000,
  });
}

export function useProperty(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id] as const,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${id}`);
      if (!res.ok) throw new Error(`property_get_failed_${res.status}`);
      return (await res.json()) as PropertyRow;
    },
    enabled: !!id,
  });
}

export function usePropertyImages(propertyId: string | undefined) {
  return useQuery({
    queryKey: ['property', propertyId, 'images'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${propertyId}/images`);
      if (!res.ok) throw new Error(`property_images_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyImageRow[] };
      return json.items;
    },
    enabled: !!propertyId,
  });
}

export interface CreatePropertyPayload {
  clientId: string;
  city: string;
  address?: string | null;
  postalCode?: string | null;
  street?: string | null;
  streetNumber?: string | null;
  entrance?: string | null;
  floor?: string | null;
  doorNumber?: string | null;
  district?: string | null;
  buildingType?: string | null;
  objectType?: string | null;
  roomsCount?: string | null;
  propertySize?: number | null;
  propertyArea?: number | null;
  purchasePrice?: number | null;
  ediRealNumber?: string | null;
  kgGrundbuch?: string | null;
  einlagezahl?: string | null;
  bLaufnummer?: string | null;
  marketingStage?: PropertyRow['marketing_stage'];
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreatePropertyPayload) => {
      const res = await apiFetch('/properties', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `property_create_failed_${res.status}`);
      }
      return (await res.json()) as PropertyRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useUpdateProperty(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Partial<CreatePropertyPayload>) => {
      const res = await apiFetch(`/properties/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `property_update_failed_${res.status}`);
      }
      return (await res.json()) as PropertyRow;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['properties'] });
      qc.invalidateQueries({ queryKey: ['property', id] });
    },
  });
}

export function useUploadPropertyImage(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { file: File; isPrimary?: boolean }) => {
      // Downsample to a sane size client-side before the upload — a 12 MB
      // iPhone JPEG becomes ~600 KB at 1920 px @ 0.86 with no visible loss.
      const { resizePropertyImage } = await import('@/lib/imageResize');
      const file = await resizePropertyImage(params.file);
      const fd = new FormData();
      fd.append('file', file);
      if (params.isPrimary) fd.append('isPrimary', 'true');
      const res = await apiFetch(`/properties/${propertyId}/images`, {
        method: 'POST',
        body: fd,
        headers: {}, // let browser set multipart boundary
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(err?.error ?? `image_upload_failed_${res.status}`);
      }
      return (await res.json()) as PropertyImageRow;
    },
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ['property', propertyId, 'images'] }),
  });
}

export function useDeletePropertyImage(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (imageId: string) => {
      const res = await apiFetch(`/properties/${propertyId}/images/${imageId}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('delete_failed');
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['property', propertyId, 'images'] });
      qc.invalidateQueries({ queryKey: ['property', propertyId] });
    },
  });
}

export interface PropertyHistoryRow {
  id: string;
  property_id: string;
  action: string;
  field_changed: string | null;
  old_value: string | null;
  new_value: string | null;
  changed_by: string | null;
  automatic_change: number;
  change_reason: string | null;
  created_at: number;
}

export function usePropertyHistory(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id, 'history'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${id}/history`);
      if (!res.ok) throw new Error(`property_history_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyHistoryRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface PropertyAppointmentRow {
  id: string;
  client_id: string;
  property_id: string | null;
  appointment_type: string;
  appointment_date: number;
  status: string;
  notes: string | null;
}

export function usePropertyAppointments(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id, 'appointments'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${id}/appointments`);
      if (!res.ok) throw new Error(`property_appointments_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyAppointmentRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

export interface PropertyOfferRow {
  id: string;
  property_id: string;
  offered_price: number | null;
  status: string;
  expires_at: number | null;
  created_at: number;
}

export function usePropertyOffers(id: string | undefined) {
  return useQuery({
    queryKey: ['property', id, 'offers'] as const,
    queryFn: async () => {
      const res = await apiFetch(`/properties/${id}/offers`);
      if (!res.ok) throw new Error(`property_offers_failed_${res.status}`);
      const json = (await res.json()) as { items: PropertyOfferRow[] };
      return json.items;
    },
    enabled: !!id,
  });
}

// Backend converts DELETE to a soft-archive.
export function useDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/properties/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`property_archive_failed_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useRestoreProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiFetch(`/properties/${id}/restore`, { method: 'POST' });
      if (!res.ok) throw new Error(`property_restore_failed_${res.status}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['properties'] }),
  });
}

export function useMatchBuyers(propertyId: string) {
  return useMutation({
    mutationFn: async (notify: boolean) => {
      const res = await apiFetch(`/properties/${propertyId}/match-buyers`, {
        method: 'POST',
        body: JSON.stringify({ notify }),
      });
      if (!res.ok) throw new Error(`buyer_match_failed_${res.status}`);
      return (await res.json()) as {
        matches: Array<{ clientId: string; score: number; matched: string[] }>;
        notified: number;
      };
    },
  });
}
